import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';
// import type { CSSObject } from '@mui/material';
import { theme } from '../../theme';
import { ActiveCueGroup, CueType } from '../StudioCues/Cue';

export const ACTIVE_CUE_CLASSNAME = 'active';
export const ACTIVE_PARAGRAPH_CLASSNAME = 'toolbar-active';
export const CUE_CLASSNAME = 'cue';
export const REPLACEMENT_CLASSNAME = 'phonetic';

// TODO: This value is currently computed and we are modifying it
// to be custom values from an array so this will need to change as well
// and may need to change per cue type
// ideally, we could get attr(initialValue) to work within attribute selectors
const INITIAL_CUE_VALUE = 0;

const getCueTypeColor = (cueType: CueType) => {
  switch (cueType) {
    case CueType.loudness:
      return theme.palette.slider.thumb.loudness.backgroundColor;
    case CueType.tempo:
      return theme.palette.slider.thumb.tempo.backgroundColor;
    case CueType.pitch:
      return theme.palette.slider.thumb.pitch.backgroundColor;
    case CueType.pause:
      return theme.palette.slider.thumb.pause.backgroundColor;
    default:
      return '';
  }
};

const generateTextDecorationCSSProperties = (attribute: CueType) => ({
  textDecoration: `underline ${getCueTypeColor(attribute)}`,
  /**
   * @note: although the console shows a warning, this is the correct way to
   * set the text decoration properties for some reason
   */
  'webkit-text-decoration-line': `underline`,
  'webkit-text-decoration-color': `${getCueTypeColor(attribute)}`,
  'webkit-text-decoration-style': `solid`,
});

const BASE_OFFSET = 5;
const SPACER_OFFSET = 2;
const generateBoxShadow = (
  attributes: Array<CueType>,
  options: {
    useSpacer?: boolean;
    baseOffset?: number;
    spacerOffset?: number;
  } = {}
) => {
  const boxShadowArray: Array<string> = [];
  const {
    useSpacer = true,
    baseOffset = BASE_OFFSET,
    spacerOffset = SPACER_OFFSET,
  } = options;

  attributes.forEach((attribute, index) => {
    if (useSpacer) {
      boxShadowArray.push(
        `0px ${baseOffset * index}px ${theme.palette.background.paper}`
      );
    }

    const boxShadowYOffset = useSpacer
      ? baseOffset * index + spacerOffset
      : baseOffset * index;

    const color = getCueTypeColor(attribute);

    boxShadowArray.push(`0px ${boxShadowYOffset}px 0px 0px ${color}`);
  });

  return boxShadowArray.join(', ');
};

const remirrorStyles: CSSObject = {
  '& .remirror-editor-wrapper': {
    height: 'auto',
    whiteSpace: 'pre-wrap',
    '& .remirror-editor': {
      height: '100%',
      position: 'relative',
      fontSize: '1.2rem',
      direction: 'inherit',
      textAlign: 'inherit',

      [`& > p.${ACTIVE_PARAGRAPH_CLASSNAME}`]: {
        background: 'rgba(255, 255, 255, 0.09)',
        borderRadius: theme.spacing(0.5),
      },
      '&:focus-visible': {
        outline: 'none',
      },
      '& .remirror-is-empty::before': {
        position: 'absolute',
        pointerEvents: 'none',
        height: 0,
        content: 'attr(data-placeholder)',
        // tertiary color
        color: '#999999',
        opacity: 1,
      },
      // todo: update underline color for each group
      [`& .${CUE_CLASSNAME}`]: {
        textUnderlineOffset: '7px',
        /**
         * Punctuation style
         */
        [`&[cuegroup="${ActiveCueGroup.pause}"]`]: {
          /**
           * Pause
           */
          [`&[pause]:not([pause="${INITIAL_CUE_VALUE}"])`]: {
            ...generateTextDecorationCSSProperties(CueType.pause),
            '& token': {
              '&::before': {
                backgroundColor: getCueTypeColor(CueType.pause),
              },
            },
          },
        },
        /**
         * Word styles
         */
        [`&[cuegroup="${ActiveCueGroup.word}"]`]: {
          /**
           * Whitespace
           */
          '& token.whitespace': {
            backgroundColor: 'transparent !important',
            cursor: 'default',
          },
          /**
           * Loudness
           */
          [`&[loudness]:not([loudness="${INITIAL_CUE_VALUE}"])`]: {
            ...generateTextDecorationCSSProperties(CueType.loudness),
            '& token': {
              '&::before': {
                backgroundColor: getCueTypeColor(CueType.loudness),
              },
            },
            [`&[pitch]:not([pitch="${INITIAL_CUE_VALUE}"])`]: {
              ...generateTextDecorationCSSProperties(CueType.loudness),
              boxShadow: generateBoxShadow([CueType.loudness, CueType.pitch]),
              '& token': {
                '&::before': {
                  boxShadow: generateBoxShadow(
                    [CueType.loudness, CueType.pitch],
                    { useSpacer: false }
                  ),
                },
              },
            },
            [`&[tempo]:not([tempo="${INITIAL_CUE_VALUE}"])`]: {
              ...generateTextDecorationCSSProperties(CueType.loudness),
              boxShadow: generateBoxShadow([CueType.loudness, CueType.tempo]),
              '& token': {
                '&::before': {
                  boxShadow: generateBoxShadow(
                    [CueType.loudness, CueType.tempo],
                    { useSpacer: false }
                  ),
                },
              },
              [`&[pitch]:not([pitch="${INITIAL_CUE_VALUE}"])`]: {
                ...generateTextDecorationCSSProperties(CueType.loudness),
                boxShadow: generateBoxShadow([
                  CueType.loudness,
                  CueType.tempo,
                  CueType.pitch,
                ]),
                '& token': {
                  '&::before': {
                    boxShadow: generateBoxShadow(
                      [CueType.loudness, CueType.tempo, CueType.pitch],
                      { useSpacer: false }
                    ),
                  },
                },
              },
            },
          },
          /**
           * Tempo
           */
          [`&[tempo]:not([tempo="${INITIAL_CUE_VALUE}"])`]: {
            ...generateTextDecorationCSSProperties(CueType.tempo),
            '& token': {
              '&::before': {
                backgroundColor: getCueTypeColor(CueType.tempo),
              },
            },
            [`&[pitch]:not([pitch="${INITIAL_CUE_VALUE}"])`]: {
              ...generateTextDecorationCSSProperties(CueType.tempo),
              boxShadow: generateBoxShadow([CueType.tempo, CueType.pitch]),
              '& token': {
                '&::before': {
                  boxShadow: generateBoxShadow([CueType.tempo, CueType.pitch], {
                    useSpacer: false,
                  }),
                },
              },
            },
          },
          /**
           * Pitch
           */
          [`&[pitch]:not([pitch="${INITIAL_CUE_VALUE}"])`]: {
            ...generateTextDecorationCSSProperties(CueType.pitch),
            '& token': {
              '&::before': {
                backgroundColor: getCueTypeColor(CueType.pitch),
              },
            },
          },
        },
      },
      [`& .${REPLACEMENT_CLASSNAME}`]: {
        [`&[enabled="true"]`]: {
          fontWeight: 'bold',
        },
        [`&[enabled="false"]`]: {
          fontWeight: 'inherit',
        },
      },
    },
  },
};

export default makeStyles<{
  isMobile: boolean;
  hasActiveCue: boolean;
  direction?: 'ltr' | 'rtl';
}>()((muiTheme, { isMobile, hasActiveCue, direction }) => {
  return {
    nonEditableContainer: {
      display: 'flex',
      flexDirection: 'row',
      [`& .${CUE_CLASSNAME}`]: {
        display: 'contents',
        '& token': {
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: theme.spacing(-0.625),
            left: 0,
            height: '2px',
            width: '100%',
          },
        },

        [`&.${ACTIVE_CUE_CLASSNAME} token`]: {
          opacity: 1,
          backgroundColor: theme.palette.accent.primaryOpaque[50],
        },
      },
    },
    container: {
      backgroundColor: theme.palette.common.black,
      border: 0,
      color: theme.palette.common.white,
      outline: 0,
      padding: 0,
      resize: 'none',
      width: '100%',
      overflowX: isMobile ? 'unset' : 'scroll',
      overflowY: 'scroll',
      ...(theme.mixins.scrollBar as CSSObject),
      ...remirrorStyles,
      ...(direction === 'rtl' && {
        direction: 'rtl',
        textAlign: 'right',
      }),
    },
    selection: {
      background: 'transparent',
      paddingBottom: theme.spacing(2),
      '& token': {
        backgroundColor: theme.palette.accent.primaryOpaque[50],
        marginRight: 0,
      },
    },
    token: {
      backgroundColor: theme.palette.grey[800],
      borderRadius: '2px',
      cursor: 'pointer',
      display: 'inline-block',
      opacity: hasActiveCue ? 0.5 : 1,
      marginBottom: theme.spacing(2),
      marginRight: 0,
      padding: '0 2px',
      '&.pause': {
        padding: '0 2px',
      },
      '&.whitespace, &.whitespace:hover': {
        backgroundColor: 'transparent',
      },
      '&:hover': {
        backgroundColor: theme.palette.accent.primaryOpaque[30],
      },
      '&:focus': {
        outline: 'unset',
      },
      '&::selection': {
        background: 'transparent !important',
      },
    },
  };
});
